import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  FileText,
  Sparkles,
  ShieldCheck,
  AlertTriangle,
  Plus,
  Loader2,
  UploadCloud,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UploadModal from "../components/UploadModal";
import FloatingChat from "../components/FloatingChat";
import SEO from "../components/SEO";
import {
  listSessionUploads,
  deleteAllSessionUploads,
  deleteUpload,
} from "../lib/uploads";
import { clearUploadConsent } from "../lib/consent";

function humanSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function relativeTime(iso) {
  const d = new Date(iso);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  const days = Math.floor(diffSec / 86400);
  if (days < 30) return `${days} d ago`;
  return d.toLocaleDateString();
}

const StatusPill = ({ status, stage }) => {
  const label =
    status === "complete"
      ? "Ready"
      : status === "failed"
      ? "Failed"
      : status === "running" && stage === "extracting"
      ? "Extracting"
      : status === "running" && stage === "analysing"
      ? "Analysing"
      : status === "running"
      ? "Working"
      : "Queued";
  const cls =
    status === "complete"
      ? "bg-emerald-50 text-emerald-700"
      : status === "failed"
      ? "bg-red-50 text-red-700"
      : status === "running"
      ? "bg-blue-50 text-blue-700"
      : "bg-slate-100 text-slate-600";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      {status === "running" && <Loader2 className="h-3 w-3 animate-spin" />}
      {status === "complete" && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
      {status === "failed" && <AlertTriangle className="h-3 w-3" />}
      {label}
    </span>
  );
};

const ScoreRing = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <div className="h-14 w-14 rounded-full border border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs">
        —
      </div>
    );
  }
  const color = value >= 75 ? "#10B981" : value >= 55 ? "#F59E0B" : "#EF4444";
  const radius = 22;
  const c = 2 * Math.PI * radius;
  const offset = c - (value / 100) * c;
  return (
    <svg width="56" height="56" viewBox="0 0 56 56">
      <circle cx="28" cy="28" r={radius} stroke="#E2E8F0" strokeWidth="5" fill="none" />
      <circle
        cx="28"
        cy="28"
        r={radius}
        stroke={color}
        strokeWidth="5"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 28 28)"
      />
      <text
        x="28"
        y="32"
        textAnchor="middle"
        fontSize="14"
        fontWeight="600"
        fill="#0F172A"
        fontFamily="Outfit"
      >
        {value}
      </text>
    </svg>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccessCount, setDeleteSuccessCount] = useState(null);
  // Per-card delete: id currently in inline-confirm mode, and set of ids
  // whose delete is in-flight.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await listSessionUploads();
      setItems(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Could not load your uploads. Please retry.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteOne = async (id) => {
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await deleteUpload(id);
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      // Surface a lightweight toast-ish error at page level.
      setError(
        e?.response?.data?.detail ||
          "Could not delete this policy. Please try again.",
      );
    } finally {
      setDeletingIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      setConfirmDeleteId(null);
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      const res = await deleteAllSessionUploads();
      setDeleteSuccessCount(res?.deleted_count ?? 0);
      // Also revoke local consent so the next upload prompts fresh consent.
      clearUploadConsent();
      setItems([]);
      setTimeout(() => {
        setDeleteConfirmOpen(false);
        setDeleteSuccessCount(null);
      }, 2500);
    } catch (e) {
      setDeleteError(
        e?.response?.data?.detail ||
          "Could not delete your data right now. Please try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Poll while anything is pending/running
  useEffect(() => {
    const hasWork = items.some(
      (u) => u.analysis_status === "pending" || u.analysis_status === "running",
    );
    if (!hasWork) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [items, load]);

  const totalPolicies = items.length;
  const completed = items.filter((i) => i.analysis_status === "complete");
  const avgClarity = completed.length
    ? Math.round(
        completed.reduce((s, i) => s + (i.clarity_score || 0), 0) /
          completed.length,
      )
    : null;

  // Ready policies for the floating Ask AI picker.
  const chatPolicies = useMemo(() => {
    if (!completed.length) return [];
    return [...completed]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .map((p) => ({
        id: p.id,
        insurer:
          p.insurer && p.insurer !== "not specified in your policy"
            ? p.insurer
            : null,
        vehicle:
          p.vehicle && p.vehicle !== "not specified in your policy"
            ? p.vehicle
            : null,
        filename: p.filename,
      }));
  }, [completed]);

  return (
    <div className="min-h-screen bg-slate-50/40">
      <SEO
        path="/dashboard"
        title="Your dashboard"
        description="Every motor insurance policy you have analysed with KnowYourPolicy.ai, private to your browser."
        noindex
      />
      <Navbar onUploadClick={() => setUploadOpen(true)} />

      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-12"
        data-testid="dashboard-page"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-500">Welcome back 👋</p>
            <h1 className="mt-1 font-display text-3xl md:text-4xl tracking-tight text-slate-900">
              Your policy dashboard
            </h1>
            <p className="mt-2 text-slate-600 max-w-xl">
              Every policy you&apos;ve uploaded from this browser, analysed and
              organised. Nothing here is shared with anyone else.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start">
            <button
              onClick={load}
              disabled={loading}
              data-testid="dashboard-refresh"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 rounded-full px-3 py-2 hover:bg-white border border-slate-200 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="dashboard-add-policy"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 font-medium shadow-sm text-sm"
            >
              <Plus className="h-4 w-4" />
              Upload policy
            </button>
          </div>
        </div>

        {/* Overview */}
        <div
          className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
          data-testid="dashboard-overview"
        >
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <FileText className="h-4 w-4" />
              </div>
              <div className="text-sm text-slate-500">Policies uploaded</div>
            </div>
            <div className="mt-3 font-display text-3xl font-semibold text-slate-900">
              {totalPolicies}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="text-sm text-slate-500">KYP Score</div>
            </div>
            <div className="mt-3 font-display text-3xl font-semibold text-slate-900">
              {avgClarity === null ? "—" : avgClarity}
              {avgClarity !== null && (
                <span className="text-base text-slate-400"> / 100</span>
              )}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div className="text-sm text-slate-500">Ready to view</div>
            </div>
            <div className="mt-3 font-display text-3xl font-semibold text-slate-900">
              {completed.length}
            </div>
          </div>
        </div>

        {/* Policies */}
        <div className="mt-12 flex items-center justify-between">
          <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900">
            Your uploaded policies
          </h2>
          {totalPolicies > 0 && (
            <Link
              to="/policies"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              data-testid="dashboard-view-all"
            >
              View all →
            </Link>
          )}
        </div>

        {loading && items.length === 0 ? (
          <div className="mt-5 border border-slate-200 rounded-2xl p-10 bg-white flex items-center justify-center text-slate-500" data-testid="dashboard-loading">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading your policies…
          </div>
        ) : error ? (
          <div className="mt-5 border border-red-200 rounded-2xl p-5 bg-red-50 text-sm text-red-700" data-testid="dashboard-error">
            {error}
          </div>
        ) : items.length === 0 ? (
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="dashboard-empty"
            className="mt-5 w-full border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/40 rounded-2xl p-12 flex flex-col items-center justify-center text-center"
          >
            <div className="h-14 w-14 rounded-full bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm">
              <UploadCloud className="h-6 w-6" />
            </div>
            <div className="mt-4 font-display text-lg font-semibold text-slate-900">
              No policies uploaded yet
            </div>
            <div className="text-sm text-slate-500 mt-1 max-w-md">
              Upload your motor insurance PDF to get a plain-English analysis
              in about 30 seconds.
            </div>
            <span className="mt-5 inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-full px-5 py-2.5 text-sm font-medium">
              <Plus className="h-4 w-4" />
              Upload your first policy
            </span>
          </button>
        ) : (
          <div
            className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="dashboard-policy-grid"
          >
            {items.map((p) => {
              const isReady = p.analysis_status === "complete";
              const title = isReady
                ? p.insurer && p.insurer !== "not specified in your policy"
                  ? p.insurer
                  : "Your policy"
                : p.filename;
              const subtitle = isReady
                ? p.vehicle && p.vehicle !== "not specified in your policy"
                  ? p.vehicle
                  : p.filename
                : `${humanSize(p.size)} · ${relativeTime(p.created_at)}`;
              const isDeleting = deletingIds.has(p.id);
              const confirming = confirmDeleteId === p.id;
              return (
                <div
                  key={p.id}
                  data-testid={`policy-card-${p.id}`}
                  className={`group relative bg-white rounded-2xl border border-slate-200 p-5 flex flex-col cursor-pointer transition-shadow ${
                    isDeleting ? "opacity-50" : "hover:shadow-md hover:border-slate-300"
                  }`}
                  role="link"
                  tabIndex={0}
                  onClick={(e) => {
                    if (confirming || isDeleting) return;
                    // Don't navigate when clicking inside the delete UI.
                    if (e.target.closest("[data-card-action]")) return;
                    navigate(`/policy/${p.id}`);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !confirming && !isDeleting) {
                      navigate(`/policy/${p.id}`);
                    }
                  }}
                >
                  {/* Delete affordance */}
                  <div
                    data-card-action="delete"
                    className={`absolute top-3 right-3 z-10 transition-opacity ${
                      confirming ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
                    }`}
                  >
                    {!confirming ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteId(p.id);
                        }}
                        disabled={isDeleting}
                        data-testid={`card-delete-btn-${p.id}`}
                        title="Delete this policy"
                        aria-label="Delete this policy"
                        className="p-1.5 rounded-full text-slate-400 hover:text-red-600 bg-white/90 hover:bg-red-50 border border-slate-200 hover:border-red-200 shadow-sm"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ) : (
                      <div
                        className="flex items-center gap-1 rounded-full bg-white border border-red-200 shadow-md pl-2 pr-1 py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="text-xs font-medium text-red-700 pl-1">
                          Delete?
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOne(p.id);
                          }}
                          disabled={isDeleting}
                          data-testid={`card-delete-confirm-${p.id}`}
                          className="rounded-full bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white text-xs font-semibold px-2.5 py-0.5"
                        >
                          {isDeleting ? "…" : "Yes"}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(null);
                          }}
                          data-testid={`card-delete-cancel-${p.id}`}
                          className="rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100 p-1"
                          aria-label="Cancel delete"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-start justify-between gap-3 pr-8">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 uppercase tracking-wider font-medium truncate">
                        {p.filename}
                      </div>
                      <h3 className="mt-1 font-display text-lg font-semibold text-slate-900 truncate">
                        {title}
                      </h3>
                      <div className="mt-0.5 text-sm text-slate-500 truncate">
                        {subtitle}
                      </div>
                    </div>
                    <ScoreRing value={isReady ? p.clarity_score : null} />
                  </div>

                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <StatusPill
                      status={p.analysis_status}
                      stage={p.analysis_stage}
                    />
                    <span
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:text-blue-700 rounded-full"
                      data-testid={`view-analysis-${p.id}`}
                    >
                      View analysis
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setUploadOpen(true)}
              data-testid="dashboard-add-card"
              className="border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50 hover:bg-blue-50/40 rounded-2xl p-8 flex flex-col items-center justify-center text-center min-h-[200px]"
            >
              <div className="h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm">
                <Plus className="h-5 w-5" />
              </div>
              <div className="mt-3 font-display font-semibold text-slate-900">
                Add another policy
              </div>
              <div className="text-sm text-slate-500 mt-1">
                PDF · Any Indian insurer
              </div>
            </button>
          </div>
        )}

        {/* Trust reminder + self-service delete */}
        <div className="mt-14 border-t border-slate-200 pt-6 pb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-3 text-sm text-slate-500 max-w-2xl">
            <ShieldCheck className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" />
            <p>
              We explain your policy — never advise. Coverage decisions rest with
              your insurer. Verify anything important against the original policy
              wording.
            </p>
          </div>
          {totalPolicies > 0 && (
            <button
              type="button"
              onClick={() => {
                setDeleteError("");
                setDeleteSuccessCount(null);
                setDeleteConfirmOpen(true);
              }}
              data-testid="dashboard-delete-data-btn"
              className="self-start inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 rounded-full px-3 py-2 hover:bg-red-50 border border-slate-200 hover:border-red-200"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete my data
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          data-testid="delete-data-overlay"
          onClick={() => !deleting && setDeleteConfirmOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-data-modal"
          >
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                  <Trash2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold text-slate-900">
                    Delete all your data?
                  </h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                    This will permanently delete{" "}
                    <strong className="text-slate-900">
                      {totalPolicies} uploaded {totalPolicies === 1 ? "policy" : "policies"}
                    </strong>
                    , the extracted text, the analysis, and any Ask AI chat
                    history tied to them. This can&apos;t be undone.
                  </p>
                </div>
              </div>

              {deleteSuccessCount !== null && !deleteError ? (
                <div
                  className="mt-5 border border-emerald-200 bg-emerald-50 rounded-xl p-3 text-sm text-emerald-800"
                  data-testid="delete-data-success"
                >
                  Deleted {deleteSuccessCount}{" "}
                  {deleteSuccessCount === 1 ? "item" : "items"}. Your dashboard
                  is now empty.
                </div>
              ) : deleteError ? (
                <div
                  className="mt-5 border border-red-200 bg-red-50 rounded-xl p-3 text-sm text-red-700"
                  data-testid="delete-data-error"
                >
                  {deleteError}
                </div>
              ) : null}

              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(false)}
                  disabled={deleting}
                  data-testid="delete-data-cancel"
                  className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50"
                >
                  {deleteSuccessCount !== null ? "Close" : "Cancel"}
                </button>
                {deleteSuccessCount === null && (
                  <button
                    type="button"
                    onClick={handleDeleteAll}
                    disabled={deleting}
                    data-testid="delete-data-confirm"
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:bg-slate-300"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Deleting…
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete everything
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={load}
      />
      {chatPolicies.length > 0 && (
        <FloatingChat policies={chatPolicies} ready={true} />
      )}
    </div>
  );
}
